import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Inbox, MailOpen, Clock, CheckCircle2, ArrowRight, Briefcase, Boxes, Building2, MessageSquareQuote, HelpCircle } from "lucide-react";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { adminDashboard } from "@/lib/api";
import { StatusBadge, formatDate } from "@/admin/shared";
import { ErrorState } from "@/components/States";

const STAT = [
  { key: "total", label: "Total inquiries", Icon: Inbox },
  { key: "new", label: "New", Icon: MailOpen, accent: true },
  { key: "in_progress", label: "In progress", Icon: Clock },
  { key: "resolved", label: "Resolved", Icon: CheckCircle2 },
];
const CONTENT = [
  { key: "services", label: "Services", Icon: Briefcase, to: "/admin/services" },
  { key: "products", label: "Products", Icon: Boxes, to: "/admin/products" },
  { key: "industries", label: "Industries", Icon: Building2, to: "/admin/industries" },
  { key: "testimonials", label: "Testimonials", Icon: MessageSquareQuote, to: "/admin/testimonials" },
  { key: "faqs", label: "FAQs", Icon: HelpCircle, to: "/admin/faqs" },
];

export default function AdminDashboardPage() {
  const { data, isLoading, isError, refetch } = useQuery({ queryKey: ["admin", "dashboard"], queryFn: adminDashboard, staleTime: 15_000 });

  if (isError) return <ErrorState onRetry={refetch} />;

  return (
    <div className="space-y-8" data-testid="admin-dashboard">
      <div><h1 className="font-heading text-2xl font-semibold tracking-tight">Dashboard</h1><p className="mt-1 text-sm text-muted-foreground">Overview of inquiries and website content.</p></div>

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {STAT.map((s) => (
          <div key={s.key} className="card-solid p-5" data-testid={`dashboard-stat-${s.key}`}>
            <div className="flex items-center justify-between"><p className="text-sm text-muted-foreground">{s.label}</p><s.Icon className={`h-4 w-4 ${s.accent ? "text-primary" : "text-muted-foreground"}`} /></div>
            {isLoading ? <Skeleton className="mt-3 h-8 w-16" /> : <p className={`mt-2 font-heading text-3xl font-semibold ${s.accent ? "text-primary" : ""}`}>{data?.inquiries?.[s.key] ?? 0}</p>}
          </div>
        ))}
      </div>

      <div className="grid gap-6 xl:grid-cols-12">
        <div className="card-solid xl:col-span-7">
          <div className="flex items-center justify-between border-b border-border px-5 py-4"><h2 className="font-heading font-semibold">Recent inquiries</h2><Button asChild variant="ghost" size="sm" className="rounded-lg"><Link to="/admin/inquiries" data-testid="dashboard-view-all-inquiries">View all <ArrowRight className="h-4 w-4" /></Link></Button></div>
          {isLoading && <div className="space-y-3 p-5">{[1, 2, 3].map((i) => <Skeleton key={i} className="h-12 w-full" />)}</div>}
          {data && data.recent_inquiries.length === 0 && <p className="p-8 text-center text-sm text-muted-foreground" data-testid="dashboard-no-inquiries">No inquiries yet. New submissions from the contact form will appear here.</p>}
          {data && data.recent_inquiries.length > 0 && (
            <ul className="divide-y divide-border">
              {data.recent_inquiries.map((q) => (
                <li key={q.id}><Link to={`/admin/inquiries?open=${q.id}`} className="flex items-center gap-4 px-5 py-3.5 transition-colors hover:bg-secondary/60" data-testid={`dashboard-inquiry-${q.id}`}>
                  <span className={`h-2 w-2 shrink-0 rounded-full ${q.is_read ? "bg-border" : "bg-primary"}`} aria-hidden="true" />
                  <div className="min-w-0 flex-1"><p className="truncate text-sm font-medium">{q.name} <span className="font-normal text-muted-foreground">&middot; {q.company || q.email}</span></p><p className="truncate text-xs text-muted-foreground">{q.subject || q.message}</p></div>
                  <StatusBadge status={q.status} />
                  <span className="hidden font-mono text-xs text-muted-foreground sm:inline">{formatDate(q.created_at)}</span>
                </Link></li>
              ))}
            </ul>
          )}
        </div>

        <div className="space-y-6 xl:col-span-5">
          <div className="card-solid p-5">
            <h2 className="font-heading font-semibold">Inquiries - last 14 days</h2>
            <div className="mt-4 h-44">
              {data && data.trend.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={data.trend} margin={{ top: 4, right: 4, left: -24, bottom: 0 }}>
                    <CartesianGrid vertical={false} stroke="#e6e8ee" />
                    <XAxis dataKey="date" tickFormatter={(d) => d.slice(5)} tick={{ fontSize: 11 }} axisLine={false} tickLine={false} />
                    <YAxis allowDecimals={false} tick={{ fontSize: 11 }} axisLine={false} tickLine={false} />
                    <Tooltip cursor={{ fill: "#f6f7f9" }} contentStyle={{ borderRadius: 12, border: "1px solid #e6e8ee", fontSize: 12 }} />
                    <Bar dataKey="count" fill="#d42137" radius={[6, 6, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              ) : <div className="flex h-full items-center justify-center text-sm text-muted-foreground">No data yet</div>}
            </div>
          </div>
          <div className="card-solid p-5">
            <h2 className="font-heading font-semibold">Website content</h2>
            <ul className="mt-3 divide-y divide-border">
              {CONTENT.map((c) => <li key={c.key}><Link to={c.to} className="flex items-center gap-3 py-3 text-sm transition-colors hover:text-primary" data-testid={`dashboard-content-${c.key}`}><c.Icon className="h-4 w-4 text-muted-foreground" /><span className="flex-1">{c.label}</span><span className="font-mono text-xs text-muted-foreground">{data?.content?.[c.key] ?? "-"}</span><ArrowRight className="h-4 w-4 text-muted-foreground" /></Link></li>)}
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}
